import { motion } from 'motion/react';

/**
 * TourSpinners — hologramas dedicados ao GuidedTour.
 *
 *  1. HoloCard: cartão Six Pass projetado em holograma — SEMPRE de frente,
 *     com leve wobble 3D (rotateY -8° a +8°), scanlines móveis, glow ciano
 *     e flicker ocasional. Usado no step 'whats-six-pass'.
 *
 *  2. HoloCoin: Six Coin gigante projetada em holograma — mesma técnica,
 *     com glow amber. Usado no step 'sixcoins'.
 *
 * Por que holograma e não 3D-spin completo:
 *  - O spin 360° mostrava o VERSO (visualmente confuso, "feio")
 *  - Wobble parcial mantém o objeto sempre legível
 *  - Estética holográfica é premium, futurista e nunca "atrapalha" a leitura
 *
 * Composição visual:
 *  - Camada base: imagem/svg do objeto, opacity 0.92
 *  - Tinta de cor (cyan/amber) via mix-blend-mode + opacity
 *  - Scanlines horizontais que descem em loop infinito
 *  - Glow radial atrás (drop-shadow + radial-gradient bg)
 *  - Wobble 3D sutil (preserve-3d + perspective parent)
 *  - Float translateY pequeno (-2 a +2px)
 *  - Flicker raro (opacity 0.92 → 1 → 0.85 a cada 7s)
 */

interface HoloCardProps {
  imageUrl: string;
  /** Largura em px. Default 180. */
  width?: number;
  className?: string;
}

export function HoloCard({ imageUrl, width = 180, className = '' }: HoloCardProps) {
  const height = width * 0.63;

  return (
    <div
      className={className}
      style={{ width, height, perspective: '800px' }}
      aria-hidden="true"
    >
      {/* Wrapper com wobble 3D + float */}
      <motion.div
        initial={{ scale: 0.6, opacity: 0, rotateY: -20 }}
        animate={{
          scale: 1,
          opacity: [0, 0.92, 1, 0.85, 1, 0.92],
          rotateY: [-8, 8, -8],
          y: [0, -3, 0, 2, 0],
        }}
        transition={{
          scale: { duration: 0.6, ease: [0.32, 1.4, 0.4, 1] },
          opacity: {
            duration: 7,
            repeat: Infinity,
            times: [0, 0.05, 0.4, 0.45, 0.55, 1],
            ease: 'easeOut',
          },
          rotateY: { duration: 5.5, repeat: Infinity, ease: 'easeInOut' },
          y: { duration: 4, repeat: Infinity, ease: 'easeInOut' },
        }}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          willChange: 'transform, opacity',
          filter:
            'drop-shadow(0 0 24px rgba(34, 211, 238, 0.55)) drop-shadow(0 0 48px rgba(168, 85, 247, 0.35))',
        }}
      >
        {/* Glow externo holográfico (sai pelos lados) */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: -10,
            borderRadius: 24,
            background:
              'radial-gradient(ellipse at center, rgba(34, 211, 238, 0.25), transparent 70%)',
            filter: 'blur(8px)',
          }}
        />

        {/* Imagem base */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 18,
            overflow: 'hidden',
            border: '1px solid rgba(34, 211, 238, 0.4)',
          }}
        >
          <img
            src={imageUrl}
            alt=""
            loading="eager"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
              opacity: 0.92,
            }}
          />

          {/* Tinta ciano holográfica via overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(135deg, rgba(34, 211, 238, 0.28), rgba(168, 85, 247, 0.18), rgba(34, 211, 238, 0.22))',
              mixBlendMode: 'screen',
              pointerEvents: 'none',
            }}
          />

          {/* Scanlines horizontais — repetidas e descendo em loop */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background:
                'repeating-linear-gradient(0deg, rgba(34, 211, 238, 0.06) 0px, rgba(34, 211, 238, 0.06) 1px, transparent 1px, transparent 4px)',
              pointerEvents: 'none',
            }}
          />

          {/* Scanline única que desce em loop (efeito "scan beam") */}
          <motion.div
            animate={{ y: ['0%', '110%'] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'linear' }}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: '14%',
              background:
                'linear-gradient(180deg, transparent, rgba(34, 211, 238, 0.35), transparent)',
              pointerEvents: 'none',
              willChange: 'transform',
            }}
          />

          {/* Border highlight no topo (efeito vidro) */}
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '40%',
              background:
                'linear-gradient(180deg, rgba(255,255,255,0.12), transparent)',
              pointerEvents: 'none',
            }}
          />
        </div>
      </motion.div>
    </div>
  );
}

interface HoloCoinProps {
  /** Tamanho em px. Default 110. */
  size?: number;
  className?: string;
}

export function HoloCoin({ size = 110, className = '' }: HoloCoinProps) {
  return (
    <div
      className={className}
      style={{ width: size, height: size, perspective: '600px' }}
      aria-hidden="true"
    >
      <motion.div
        initial={{ scale: 0.4, opacity: 0, rotateY: -25 }}
        animate={{
          scale: 1,
          opacity: [0, 0.95, 1, 0.85, 1, 0.95],
          rotateY: [-10, 10, -10],
          y: [0, -2, 0, 2, 0],
        }}
        transition={{
          scale: { duration: 0.7, ease: [0.32, 1.6, 0.4, 1] },
          opacity: {
            duration: 6,
            repeat: Infinity,
            times: [0, 0.05, 0.4, 0.45, 0.55, 1],
            ease: 'easeOut',
          },
          rotateY: { duration: 4.5, repeat: Infinity, ease: 'easeInOut' },
          y: { duration: 3.5, repeat: Infinity, ease: 'easeInOut' },
        }}
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          willChange: 'transform, opacity',
          filter:
            'drop-shadow(0 0 18px rgba(251, 191, 36, 0.7)) drop-shadow(0 0 36px rgba(251, 191, 36, 0.35))',
        }}
      >
        {/* Glow externo ao redor */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: -8,
            borderRadius: '50%',
            background:
              'radial-gradient(circle at center, rgba(251, 191, 36, 0.35), transparent 70%)',
            filter: 'blur(6px)',
          }}
        />

        {/* SVG coin face — sempre frente */}
        <svg
          viewBox="0 0 40 40"
          width="100%"
          height="100%"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'block',
          }}
        >
          <defs>
            <radialGradient id="holoCoinFace" cx="32%" cy="30%" r="80%">
              <stop offset="0%" stopColor="#fef3c7" />
              <stop offset="30%" stopColor="#fcd34d" />
              <stop offset="65%" stopColor="#f59e0b" />
              <stop offset="100%" stopColor="#b45309" />
            </radialGradient>
            <linearGradient id="holoCoinRim" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#fde68a" />
              <stop offset="100%" stopColor="#92400e" />
            </linearGradient>
            <radialGradient id="holoCoinSheen" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255, 255, 255, 0.3)" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
          </defs>
          {/* Anel externo */}
          <circle cx="20" cy="20" r="19" fill="url(#holoCoinRim)" opacity="0.95" />
          {/* Face interna */}
          <circle cx="20" cy="20" r="16" fill="url(#holoCoinFace)" opacity="0.95" />
          {/* Brilho catch-light */}
          <ellipse cx="14" cy="13" rx="5" ry="3" fill="white" opacity="0.5" />
          {/* "6" central */}
          <text
            x="20"
            y="28"
            textAnchor="middle"
            fontSize="22"
            fontFamily="-apple-system, BlinkMacSystemFont, sans-serif"
            fontWeight="900"
            fill="#7c2d12"
          >
            6
          </text>
          {/* Overlay holográfico interno (sheen rotativo) */}
          <circle cx="20" cy="20" r="19" fill="url(#holoCoinSheen)" />
        </svg>

        {/* Scanlines circulares — leve overlay */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            background:
              'repeating-linear-gradient(0deg, rgba(251, 191, 36, 0.08) 0px, rgba(251, 191, 36, 0.08) 1px, transparent 1px, transparent 5px)',
            mixBlendMode: 'screen',
            pointerEvents: 'none',
            overflow: 'hidden',
            mask: 'radial-gradient(circle, black 95%, transparent 100%)',
            WebkitMask: 'radial-gradient(circle, black 95%, transparent 100%)',
          }}
        />

        {/* Scan beam descendo */}
        <motion.div
          animate={{ y: ['-20%', '120%'] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'linear' }}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: '18%',
            background:
              'linear-gradient(180deg, transparent, rgba(255, 251, 235, 0.5), transparent)',
            mixBlendMode: 'screen',
            pointerEvents: 'none',
            borderRadius: '50%',
            willChange: 'transform',
          }}
        />
      </motion.div>
    </div>
  );
}
